import { UNSTABLE_ToastQueue as RACToastQueue } from 'react-aria-components';
import { flushSync } from 'react-dom';
import type { InternalToastContent, UNSTABLE_ToastOptions } from './types.js';

const MIN_TOAST_TIMEOUT = 5000;

export const internalToastQueue = new RACToastQueue<InternalToastContent>({
  wrapUpdate(fn) {
    if ('startViewTransition' in document) {
      document.startViewTransition(() => {
        flushSync(fn);
      });
    } else {
      fn();
    }
  },
});

function addToast(content: InternalToastContent, options?: UNSTABLE_ToastOptions) {
  if (options?.timeout != null && options.timeout > 0 && options?.timeout <= MIN_TOAST_TIMEOUT) {
    console.warn(
      `[ToastQueue] The specified timeout (${options.timeout} ms) is less than the minimum allowed (${MIN_TOAST_TIMEOUT} ms). It will be adjusted to the minimum.`,
    );
  }

  const timeout = Math.max(options?.timeout ?? MIN_TOAST_TIMEOUT, MIN_TOAST_TIMEOUT);

  return internalToastQueue.add(content, { ...options, timeout });
}

/**
 * Manages the queue of toasts.
 */
export interface UNSTABLE_ToastQueue {
  /**
   * Add an info toast to the queue.
   * @returns The ID of the toast
   */
  info(message: string, options?: UNSTABLE_ToastOptions): string;
  /**
   * Add a success toast to the queue.
   * @returns The ID of the toast
   */
  success(message: string, options?: UNSTABLE_ToastOptions): string;
  /**
   * Add a warning toast to the queue.
   * @returns The ID of the toast
   */
  warning(message: string, options?: UNSTABLE_ToastOptions): string;
  /**
   * Add a critical toast to the queue.
   * @returns The ID of the toast
   */
  critical(message: string, options?: UNSTABLE_ToastOptions): string;
  /**
   * Close a toast.
   * @param id The ID of the toast to close
   */
  close(id: string): void;
  /**
   * Clear the queue.
   */
  clear(): void;
}

export const UNSTABLE_toastQueue: UNSTABLE_ToastQueue = {
  info(message: string, options?: UNSTABLE_ToastOptions) {
    return addToast(
      {
        message: message,
        tone: 'info',
      },
      options,
    );
  },
  success(message: string, options?: UNSTABLE_ToastOptions) {
    return addToast(
      {
        message: message,
        tone: 'success',
      },
      options,
    );
  },
  warning(message: string, options?: UNSTABLE_ToastOptions) {
    return addToast(
      {
        message: message,
        tone: 'warning',
      },
      options,
    );
  },
  critical(message: string, options?: UNSTABLE_ToastOptions) {
    return addToast(
      {
        message: message,
        tone: 'critical',
      },
      options,
    );
  },
  close(id: string) {
    internalToastQueue.close(id);
  },
  clear() {
    internalToastQueue.clear();
  },
};
